// src/components/Login/ProfessionalLogin.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import { PageContainer } from './styles/StyledComponents';
import { HEALTH_GREEN } from './utils/mockData';
import LoginView from './views/LoginView';
import RegisterView from './views/RegisterView';
import ForgotPasswordView from './views/ForgotPasswordView';
import LoginViewIndependent from './views/LoginViewIndependent';
import RegisterViewIndependent from './views/RegisterViewIndependent';
import ForgotPasswordViewIndependent from './views/ForgotPasswordViewIndependent';
import ProfessionalDashboard from '../pages/dashboard/ProfessionalDashboard';
import SubscriptionGuard from '../Subscription/SubscriptionGuard';
import SubscriptionPromo from '../Subscription/SubscriptionPromo';

// Main component
const ProfessionalLogin = ({ onUpdateUser }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState('login');
  const [showIntro, setShowIntro] = useState(true);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [redirectToDashboard, setRedirectToDashboard] = useState(false);
  const [userType, setUserType] = useState('center'); // 'center' o 'independent'
  const [showPromo, setShowPromo] = useState(false);

  // Animation complete handler
  const handleAnimationComplete = () => {
    setTimeout(() => {
      setShowIntro(false);
    }, 500);
  };

  // Register handler - Start registration flow
  const handleStartRegister = () => {
    setActiveTab('register');
  };

  // Forgot password handler
  const handleForgotPassword = () => {
    setActiveTab('forgotPassword');
  };

  // Back to login handler
  const handleBackToLogin = () => {
    setActiveTab('login');
  };

  // Login success handler
  const handleLoginSuccess = (user) => {
    setLoggedInUser(user);

    if (onUpdateUser) {
      onUpdateUser(user);
    }
  
    // Si es un usuario independiente, verificar si necesita mostrar promo
    if (userType === 'independent') {
      const token = localStorage.getItem('agthealth_token');
      if (token) {
        try {
          const tokenData = JSON.parse(atob(token.split('.')[1]));
          // Solo mostrar promo si NO es usuario Pro
          if (!tokenData.isPro) {
            setShowPromo(true);
            return;
          }
        } catch (error) {
          console.error('Error al decodificar token:', error);
        }
      }
    }
  
    setShowWelcome(true);
  
    // Update user in parent component if callback provided
    if (onUpdateUser) {
      onUpdateUser(user);
    }
  
    // After 3 seconds, redirect to dashboard
    setTimeout(() => {
      setShowWelcome(false);
      setRedirectToDashboard(true);
    }, 3000);
  };
  
  // Si el usuario es independiente y está recién registrado, mostrar promo
  useEffect(() => {
    if (loggedInUser && userType === 'independent' && !redirectToDashboard) {
      // Verificar si el usuario necesita ver la promo
      const token = localStorage.getItem('agthealth_token');
      if (token) {
        try {
          const tokenData = JSON.parse(atob(token.split('.')[1]));
          // Solo mostrar promo si NO es usuario Pro
          if (!tokenData.isPro) {
            setShowPromo(true);
          }
        } catch (error) {
          console.error('Error al decodificar token:', error);
        }
      }
    }
  }, [loggedInUser, userType, redirectToDashboard]);

  // Función para manejar el cierre de la promo
  const handleClosePromo = () => {
    setShowPromo(false);
    // Redirigir al dashboard (con período de prueba)
    setRedirectToDashboard(true);
  };

  // If showing intro animation
  if (showIntro) {
    return (
      <PageContainer>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%'
        }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            onAnimationComplete={handleAnimationComplete}
          >
            <Typography 
              variant={isMobile ? "h4" : "h3"} 
              sx={{ 
                fontWeight: 600, 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              AGT Health
              <motion.span
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8, duration: 0.5, type: "spring" }}
                style={{ 
                  color: HEALTH_GREEN, 
                  fontSize: isMobile ? '24px' : '32px', 
                  fontWeight: 700, 
                  marginLeft: '2px',
                  position: 'relative',
                  top: '-8px'
                }}
              >
                +
              </motion.span>
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ mt: 2, textAlign: 'center' }}
            >
              For Healthcare Professionals
            </Typography>
          </motion.div>
        </Box>
      </PageContainer>
    );
  }

  // Si se muestra la promo después del registro
  if (showPromo && loggedInUser) {
    return (
      <PageContainer>
        <SubscriptionPromo 
          onClose={handleClosePromo} 
          onSuccess={() => {
            setShowPromo(false);
            setRedirectToDashboard(true);
          }}
        />
      </PageContainer>
    );
  }

  // If redirecting to dashboard, render the dashboard component
  if (redirectToDashboard && loggedInUser) {
    // Si es usuario independiente, envolver en SubscriptionGuard
    if (userType === 'independent') {
      return (
        <SubscriptionGuard>
          <ProfessionalDashboard 
            user={loggedInUser} 
            onSignOut={() => {
              setLoggedInUser(null);
              setRedirectToDashboard(false);
              setActiveTab('login');
              localStorage.removeItem('agthealth_token');
            }} 
          />
        </SubscriptionGuard>
      );
    }
    
    // Para usuarios de centros, mostrar dashboard normal
    return (
      <ProfessionalDashboard 
        user={loggedInUser} 
        onSignOut={() => {
          setLoggedInUser(null);
          setRedirectToDashboard(false);
          setActiveTab('login');
          localStorage.removeItem('agthealth_token');
        }} 
      />
    );
  }

  // If user is logged in, show a welcome message (this would typically redirect to the main app)
  if (showWelcome && loggedInUser) {
    return (
      <PageContainer>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%'
        }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Typography 
              variant={isMobile ? "h4" : "h3"} 
              sx={{ 
                fontWeight: 600, 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              Welcome, {loggedInUser.firstName || 'Doctor'}!
            </Typography>
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <Typography 
                  variant="body1" 
                  color="text.secondary" 
                  sx={{ textAlign: 'center' }}
                >
                  Redirecting to your dashboard...
                </Typography>
              </motion.div>
            </Box>
          </motion.div>
        </Box>
      </PageContainer>
    );
  }

  // Render forgot password flow
  if (activeTab === 'forgotPassword') {
    return (
      <PageContainer>
        {userType === 'center' ? (
          <ForgotPasswordView 
            onBackToLogin={handleBackToLogin}
            isMobile={isMobile}
            userType={userType}
            onUserTypeChange={setUserType}
          />
        ) : (
          <ForgotPasswordViewIndependent 
            onBackToLogin={handleBackToLogin}
            isMobile={isMobile}
            userType={userType}
            onUserTypeChange={setUserType}
          />
        )}
      </PageContainer>
    );
  }

  // Render registration flow
  if (activeTab === 'register') {
    return (
      <PageContainer>
        {userType === 'center' ? (
          <RegisterView 
            onBackToLogin={handleBackToLogin}
            isMobile={isMobile}
            userType={userType}
            onUserTypeChange={setUserType}
          />
        ) : (
          <RegisterViewIndependent 
            onBackToLogin={handleBackToLogin}
            isMobile={isMobile}
            userType={userType}
            onUserTypeChange={setUserType}
          />
        )}
      </PageContainer>
    );
  }

  // Render login form
  return (
    <PageContainer>
      {userType === 'center' ? (
        <LoginView 
          onStartRegister={handleStartRegister}
          onForgotPassword={handleForgotPassword}
          onLoginSuccess={handleLoginSuccess}
          isMobile={isMobile}
          userType={userType}
          onUserTypeChange={setUserType}
        />
      ) : (
        <LoginViewIndependent 
          onStartRegister={handleStartRegister}
          onForgotPassword={handleForgotPassword}
          onLoginSuccess={handleLoginSuccess}
          isMobile={isMobile}
          userType={userType}
          onUserTypeChange={setUserType}
        />
      )}
    </PageContainer>
  );
};

export default ProfessionalLogin;